import { Map, MapBrowserEvent, View } from "ol";
import { ListenerFunction } from "ol/events";
import { CustomMapEvents } from "../../voyage-map/src/voyage-ol-map";

enum PanDirection {
    N = "north",
    NE = "north-east",
    E = "east",
    SE = "south-east",
    S = "south",
    SW = "south-west",
    W = "west",
    NW = "north-west",
}

export class MapEdgePanningInteraction {

    constructor(map: Map) {
        this._map = map;
        this._view = map.getView();

        this._onPointerMove = this._onPointerMove.bind(this);
    }

    get modeIsActive() {
        return this._panningModeIsActive;
    }

    enableEdgePanning() {
        this._map!.addEventListener('pointermove', <ListenerFunction>this._onPointerMove);
        this._panningModeIsActive = true;
    }

    disableEdgePanningMode() {
        this._map!.removeEventListener('pointermove', <ListenerFunction>this._onPointerMove);
        this._view!.cancelAnimations();
        this._panningModeIsActive = false;
    }

    private _map: Map;
    private _view: View;
    private _moveCount: number = 0;
    private _currentDirection: PanDirection|null = null;
    private _panningModeIsActive: boolean = false;

    _panMap(direction: PanDirection) {
        if (this._currentDirection === direction) {
            return;
        }
        
        const currentCenter = this._view?.getCenter();
        if (currentCenter) {
            const viewPort = this._view!.calculateExtent([this._map!.getViewport().clientWidth, this._map!.getViewport().clientHeight]);
            // 28 is just a number found from testing various numbers until pan speed seemed right
            const horizontalDistance = (viewPort[2] - viewPort[0])*28;
            const verticalDistance = (viewPort[3] - viewPort[1])*28;
            const speed = 0.5;
            
            // The duration of the pan can be any number. It's just
            // important to keep it to a long duration, because that
            // makes a smooth motion (instead of starting a new animation
            // frequently). The panning animation will be cancelled whenever mouse leaves area
            const panDuration = 60000;

            let horizontalDirection = 0;
            let verticalDirection = 0;
            switch (direction) {
                case PanDirection.N:
                    verticalDirection = verticalDistance;
                    break;
                case PanDirection.NE:
                    verticalDirection = verticalDistance;
                    horizontalDirection = horizontalDistance;
                    break;
                case PanDirection.E:
                    horizontalDirection = horizontalDistance;
                    break;
                case PanDirection.SE:
                    horizontalDirection = -horizontalDistance;
                    verticalDirection =  -verticalDistance;
                    break;
                case PanDirection.S:
                    verticalDirection =  -verticalDistance;
                    break;
                case PanDirection.SW:
                    verticalDirection =  -verticalDistance;
                    horizontalDirection = horizontalDistance;
                    break;
                case PanDirection.W:
                    horizontalDirection = -horizontalDistance;
                    break;
                case PanDirection.NW:
                    horizontalDirection = -horizontalDistance;
                    verticalDirection =  verticalDistance;
                    break;
                default:
                    console.error("Cannot calculate pan direction.", "Direction received: ", direction);
                    return;
            }
            
            const newCenter = [currentCenter[0]+horizontalDirection, currentCenter[1]+verticalDirection]
            
            // if mouse goes directly from one pan area to another (e.g. from N to NW),
            // then animations need to be cancelled before a new one can start
            if (direction !== this._currentDirection) {
                this._view!.cancelAnimations();
                this._currentDirection = direction;
            }
            this._view!.animate({center: newCenter, duration: panDuration, easing: (e) => {return e*speed}}, () => this._map?.dispatchEvent(CustomMapEvents.EndOfAnimation));
        }
    }

    private _handleMapEdgePanning(pixel: number[]) {
        const viewPixelsSize = this._map!.getSize()!
        const direction = this._getMouseEdgeDirection(pixel[0], pixel[1], viewPixelsSize[0], viewPixelsSize[1]);

        if (direction) {
            this._panMap(direction)
        } else {
            this._currentDirection = null;
            this._map!.getView().cancelAnimations();
        }
    }

    private _getMouseEdgeDirection(mouseX: number, mouseY: number, width: number, height: number): PanDirection|null {
        const edgeDist = 70;
        const topEdgeDist = 10+edgeDist; // Start panning after buttons below menu (in main-app). 10px to account for the button height
        const leftEdgeDist = 365+edgeDist; // Start panning after left UI (route editor). 365px is the width of route-editor container
        const w = width - edgeDist
        const h = height - edgeDist;

        if (mouseX > w && mouseY < topEdgeDist) return PanDirection.NE;
        if (mouseX > w && mouseY > h) return PanDirection.SW;
        if (mouseX < leftEdgeDist && mouseY > h) return PanDirection.SE;
        if (mouseX < leftEdgeDist && mouseY < topEdgeDist) return PanDirection.NW;

        if (mouseX < leftEdgeDist) return PanDirection.W;
        if (mouseY < topEdgeDist) return PanDirection.N;
        if (mouseX > w) return PanDirection.E;
        if (mouseY > h) return PanDirection.S;

        return null;
    }

    private _onPointerMove(evt: MapBrowserEvent<UIEvent>)
    {
        if (!this._panningModeIsActive) {
            return;
        }
        // Process every 5 move events only
        this._moveCount++;
        if (this._moveCount != 5) { return; }
        this._moveCount = 0;
        
        const pixel = this._map!.getEventPixel(evt.originalEvent);

        this._handleMapEdgePanning(pixel)

        return true;
    }
}
